import { SINGLE_TYPE } from 'core/constants'
import { usePurchase } from 'core/logic/purchase/purchase.hook'
import { analyticsEvent } from 'core/modules/analytics/events'
import {
  TO_DROP_OPEN,
  TO_DROP_OPENING,
  TO_MY_COLLECTION,
} from 'core/modules/router'
import {
  ComponentProps,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import PaymentView from './PendingPayment.view'
import { PurchaseType } from 'core/logic/purchase/purchase.types'
import { OfferType } from 'core/logic/drop/drop.types'
import { useSaleAvailableOnMarketplace } from 'context/Marketplace'
import { CurrenciesEnum } from 'core/types/CurrenciesEnum'
import { useAsset } from 'context/Asset'
import useRouteGalleryOrDefault from 'openspace/hooks/useRoutePathGallery'
import { usePaymentStatusOverrideForStripe } from './PendingPayment.hook'

const PendingPaymentLogic = () => {
  const history = useHistory()
  const goToPackOpen = useCallback(
    (deliveryId: string, TO_DROP: (dId: string) => string) =>
      history.push(TO_DROP(deliveryId)),
    [history]
  )
  const onLeaveClickHandler = () => history.push(TO_MY_COLLECTION())
  const uuid = uuidv4()

  const { purchaseId } = useParams<{ purchaseId: string }>()
  const { purchase } = usePurchase({ purchaseId })
  const deliveryId = purchase?.deliveryId
  const drop = purchase?.offer

  const paymentStatus = usePaymentStatusOverrideForStripe(purchase)
  const _purchase = useMemo(() => {
    if (!purchase) return null
    return {
      ...purchase,
      paymentStatus,
    } as PurchaseType
  }, [purchase, paymentStatus])

  const gotToPackSingle = useCallback(
    (type: OfferType['type'], deliveryId: string) => {
      return type === SINGLE_TYPE || type === "cvm88"
        ? goToPackOpen(deliveryId, TO_DROP_OPENING)
        : goToPackOpen(deliveryId, TO_DROP_OPEN)
    },
    [goToPackOpen]
  )

  // Listens for the deliveryId being added to the purchase, goes to opening when it does
  useEffect(() => {
    if (deliveryId) {
      if (drop) {
        analyticsEvent.paymentConfirmed({ deliveryId, offer: drop })
        gotToPackSingle(drop.type, deliveryId)
      } else {
        goToPackOpen(deliveryId, TO_DROP_OPENING)
      }
    }
  }, [gotToPackSingle, drop, deliveryId])

  const PaymentView = purchase?.nextAction
    ? PurchaseTypeBasedPaymentView.MarketplacePurchase
    : PurchaseTypeBasedPaymentView.OfferPurchase

  const gallery = useRouteGalleryOrDefault()
  return (
    <PaymentView
      drop={drop}
      purchase={_purchase}
      onMyCollection={onLeaveClickHandler}
      uuid={uuid}
      gallery={gallery}
    />
  )
}

const PurchaseTypeBasedPaymentView = {
  MarketplacePurchase: (props) => {
    const purchase = props.purchase!
    const associatedSale = useSaleAvailableOnMarketplace(
      purchase?.nextAction?.data.id!
    )
    const associatedAsset = useAsset('id', [associatedSale?.assetId!])
    const purchaseWithDerivedOffer = useMemo(() => {
      if (!associatedSale || !associatedAsset) return null
      return {
        ...purchase,
        offer: {
          currency: CurrenciesEnum.BRL,
          unitPrice: associatedSale.price,
          asset: associatedAsset,
        } as OfferType,
      }
    }, [associatedSale, associatedAsset, purchase])

    return (
      <PaymentView
        {...props}
        purchase={purchaseWithDerivedOffer}
        drop={purchaseWithDerivedOffer?.offer}
      />
    )
  },
  OfferPurchase: (props) => {
    return <PaymentView {...props} />
  },
} satisfies {
  [k: string]: (props: ComponentProps<typeof PaymentView>) => ReactElement
}

export default PendingPaymentLogic
